"use client";

import { useActionState, useEffect } from "react";
import Link from "next/link";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { login, type LoginResult } from "@/features/auth/session/auth.login-action";
import { cn } from "@/lib/utils";

function buildOAuthUrl(
  provider: "google" | "microsoft",
  redirectTo: string,
): string {
  const base = `/api/auth/oauth/${provider}`;
  const params = new URLSearchParams({ redirect: redirectTo });
  return `${base}?${params.toString()}`;
}

export function LoginForm({
  redirectTo,
  oauthError = null,
}: {
  redirectTo: string;
  oauthError?: string | null;
}) {
  const [state, formAction] = useActionState<LoginResult | null, FormData>(
    login,
    null,
  );

  useEffect(() => {
    if (state?.success) {
      // Hard navigation so login does not depend on Next soft-redirect RSC fetch
      // (which can HeadersTimeout under a cold/slow next dev).
      window.location.assign(state.redirectTo);
    }
  }, [state]);

  return (
    <Card className="w-full max-w-md border-0 shadow-lg sm:border sm:shadow-xl">
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="text-2xl font-semibold">Sign in</CardTitle>
        <CardDescription>
          Enter your email and password to access Cleos
        </CardDescription>
      </CardHeader>
      <form action={formAction}>
        <CardContent className="space-y-4">
          <input type="hidden" name="redirect" value={redirectTo} />
          {oauthError && (
            <div
              role="alert"
              className={cn(
                "rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive",
              )}
            >
              {oauthError}
            </div>
          )}
          {state && !state.success && (
            <div
              role="alert"
              className={cn(
                "rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive",
              )}
            >
              {state.error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="your@clinic.com"
              autoComplete="email"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              {/* <Link
                href="/login/forgot-password"
                className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
              >
                Forgot password?
              </Link> */}
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder="*************"
              required
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-6 pt-8">
          <Button type="submit" className="w-full" size="lg">
            Sign in with email
          </Button>
          <p className="text-muted-foreground text-center text-xs">
            Don&apos;t have an account?{" "}
            <Link
              href="/register"
              className="underline underline-offset-4 hover:text-foreground"
            >
              Register
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
